/* 
  File: PriorityDropdown.tsx
  Description: A dropdown component for selecting task priority.
*/
import { Adapt, getTokens, Popover, Text, XStack, YStack } from '@my/ui'
import { Flag as FlagIcon } from '@tamagui/lucide-icons'
import { Priority } from 'app/data/types/tasks'
import { View } from 'react-native'

interface PriorityDropdownProps {
  value: Priority
  onChange: (value: Priority) => void
}

const options: { label: string; fill: string; color: string; value: Priority }[] = [
  { label: 'ASAP', fill: 'purple8Light', color: 'purple8Light', value: 'asap' },
  { label: 'High', fill: 'red8Light', color: 'red8Light', value: 'high' },
  { label: 'Medium', fill: 'yellow8Light', color: 'yellow8Light', value: 'medium' },
  { label: 'Low', fill: 'blue8Light', color: 'blue8Light', value: 'low' },
  { label: 'None', fill: '', color: 'gray8Light', value: null },
]

export function PriorityDropdown({ value, onChange }: PriorityDropdownProps) {
  const selectedOption = options.find((option) => option.value === value) || options[4] // Default to 'None'

  const getFlagIconProps = (option: (typeof options)[0]) => {
    const tokenColor = getTokens().color[option.fill]?.val || option.color
    return {
      size: 16,
      strokeWidth: 3,
      ...(option.fill ? { fill: tokenColor } : {}),
      ...(option.color ? { color: tokenColor } : {}),
    }
  }

  return (
    <Popover size="$5" allowFlip>
      <Popover.Trigger asChild>
        <XStack
          borderWidth={selectedOption.label === 'None' ? 2.5 : 2.5}
          borderColor={selectedOption.label === 'None' ? '$color5' : '$accentColor'}
          paddingVertical="$1.5"
          paddingHorizontal="$2.5"
          borderRadius="$2"
          alignItems="center"
          alignSelf="flex-start"
          flexShrink={1}
          flexGrow={0}
          maxWidth="100%"
          cursor="pointer"
          hoverStyle={{ backgroundColor: '$color1' }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {selectedOption.label === 'None' ? (
              <FlagIcon color="$color6" size={16} strokeWidth={2.5} />
            ) : (
              <FlagIcon {...getFlagIconProps(selectedOption)} />
            )}
            <Text
              fontSize="$3"
              fontWeight={selectedOption.label === 'None' ? '500' : '500'}
              color={selectedOption.label === 'None' ? '$color6' : '$accentColor'}
              style={{ marginLeft: 8 }}
            >
              {selectedOption.label === 'None' ? 'Priority' : selectedOption.label}
            </Text>
          </View>
        </XStack>
      </Popover.Trigger>

      <Popover.Content
        borderWidth={1}
        padding={0}
        borderColor="$borderColor"
        enterStyle={{ y: -2, opacity: 0 }}
        exitStyle={{ y: -2, opacity: 0 }}
        elevate
        backgroundColor="$color1"
        shadowColor="$color10"
        shadowRadius={10}
        shadowOffset={{ width: 0, height: 0 }}
        shadowOpacity={0.2}
        animation={[
          'quick',
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
      >
        <YStack paddingTop="$4" paddingBottom="$5" width={130} alignSelf="center">
          {options.map((item) => (
            <Popover.Close asChild key={item.label}>
              <XStack
                onPress={() => onChange(item.value)}
                paddingVertical="$3"
                paddingHorizontal="$5"
                hoverStyle={{ backgroundColor: '$color1' }}
                cursor="pointer"
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                  <View style={{ width: 20, marginRight: 3, marginLeft: 3 }}>
                    {item.label === 'None' ? (
                      <FlagIcon color="$color6" size={16} strokeWidth={2} />
                    ) : (
                      <FlagIcon {...getFlagIconProps(item)} />
                    )}
                  </View>
                  <Text fontSize="$4">{item.label}</Text>
                </View>
              </XStack>
            </Popover.Close>
          ))}
        </YStack>
      </Popover.Content>

      <Popover.Adapt when="sm">
        <Popover.Sheet modal dismissOnSnapToBottom snapPointsMode="fit" animation="200ms">
          <Popover.Sheet.Frame padding="$3">
            <Adapt.Contents />
          </Popover.Sheet.Frame>
          <Popover.Sheet.Overlay
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
        </Popover.Sheet>
      </Popover.Adapt>
    </Popover>
  )
}
